import React, { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../store";
import { fetchAgentByIdentifier } from "../store/agentSlice";
import PageLoader from "../components/PageLoader";

const RequireAgentAccess: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { identifier } = useParams();

  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent
  );
  const agentsLoading = useSelector(
    (state: RootState) => state.agents.currentAgentLoading
  );
  const agentError = useSelector(
    (state: RootState) => state.agents.currentAgentError
  );

  // Fetch agent by identifier (handles both ID and framework_name)
  useEffect(() => {
    if (identifier) {
      dispatch(fetchAgentByIdentifier(identifier));
    }
  }, [identifier, dispatch]);

  // Show loader while agent is being fetched
  if (agentsLoading) {
    return <PageLoader label="Loading session..." activeNav="home" />;
  }

  // Handle case where agent identifier is invalid or not found
  if (agentError && !agentsLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" component="h1">
          Agent Not Found
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 520 }}>
          The agent you're looking for could not be found. Please check the URL
          and try again.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/landing", { replace: true })}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  // Check if user has access to this agent
  if (currentAgent && currentAgent.is_access === 0) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" component="h1">
          Access Restricted
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 520 }}>
          You do not have access to this agent. Please contact your
          administrator to request access.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/landing", { replace: true })}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
};

export default RequireAgentAccess;
